import { isAxiosError } from "axios";
import { api } from "@/lib/apiClient";

export type InvestorSettlementEntry = {
  id: number;
  debit: number;
  credit: number;
  amount: number;
  entry_type: string;
  entry_date: string | null;
  product_id: number | null;
  product_name?: string | null;
  description?: string | null;
  notes?: string | null;
};

export type InvestorSettlementSummary = {
  profit_distribution_id: number;
  party_id: number;
  final_payable: number;
  paid_amount: number;
  balance: number;
  allocated_active: number;
  available: number;
  allocations: InvestorSettlementEntry[];
};

export type InvestorSettlementPayload = {
  profit_distribution_id: number;
  refund_amount?: number;
  reinvest_amount?: number;
  destination_product_id?: number | null;
  destination_description?: string | null;
  allocated_at?: string | null;
  notes?: string | null;
  payment_date?: string | null;
  payment_method?: string | null;
  bank_id?: number | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  cheque_no?: string | null;
  deposit_slip_no?: string | null;
};

export function investorSettlementErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong while saving the investor settlement.";
}

export async function fetchInvestorSettlementSummary(
  productId: string | number,
  profitDistributionId: number,
): Promise<InvestorSettlementSummary> {
  const response = await api.get<{ data: InvestorSettlementSummary }>(
    `/api/v1/catalog/products/${encodeURIComponent(String(productId))}/investor-settlement-summary`,
    { params: { profit_distribution_id: profitDistributionId } },
  );
  const data = response.data?.data;
  return {
    profit_distribution_id: Number(data?.profit_distribution_id ?? profitDistributionId),
    party_id: Number(data?.party_id ?? 0),
    final_payable: Math.round(Number(data?.final_payable ?? 0) * 100) / 100,
    paid_amount: Math.round(Number(data?.paid_amount ?? 0) * 100) / 100,
    balance: Math.round(Number(data?.balance ?? 0) * 100) / 100,
    allocated_active: Math.round(Number(data?.allocated_active ?? 0) * 100) / 100,
    available: Math.round(Number(data?.available ?? 0) * 100) / 100,
    allocations: Array.isArray(data?.allocations)
      ? data.allocations.map((row) => ({
          id: Number(row.id),
          debit: Math.round(Number(row.debit ?? 0) * 100) / 100,
          credit: Math.round(Number(row.credit ?? 0) * 100) / 100,
          amount: Math.round(Number(row.amount ?? row.debit ?? row.credit ?? 0) * 100) / 100,
          entry_type: String(row.entry_type ?? ""),
          entry_date: row.entry_date ?? null,
          product_id: row.product_id != null ? Number(row.product_id) : null,
          product_name: row.product_name ?? null,
          description: row.description ?? null,
          notes: row.notes ?? null,
        }))
      : [],
  };
}

export async function recordInvestorSettlement(
  productId: string | number,
  payload: InvestorSettlementPayload,
): Promise<unknown> {
  const response = await api.post(
    `/api/v1/catalog/products/${encodeURIComponent(String(productId))}/investor-settlements`,
    payload,
  );
  return response.data;
}
